import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { hotelEmailTemplateService } from "@/services/hotel-email-template-service";
import type {
  EmailTemplateTrigger,
  HotelEmailTemplate,
  PreviewHotelEmailTemplatePayload,
  RenderedHotelEmailTemplate,
  UpsertHotelEmailTemplatePayload,
} from "@/types/hotel-email-template";

export const hotelEmailTemplateKeys = {
  all: ["hotelEmailTemplates"] as const,
  list: (hotelId: string | null) =>
    [...hotelEmailTemplateKeys.all, hotelId ?? ""] as const,
};

export const useHotelEmailTemplates = (hotelId: string | null) => {
  return useQuery<HotelEmailTemplate[]>({
    queryKey: hotelEmailTemplateKeys.list(hotelId),
    queryFn: () => hotelEmailTemplateService.list(hotelId as string),
    enabled: Boolean(hotelId),
  });
};

export const useUpsertHotelEmailTemplate = (hotelId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      trigger,
      payload,
    }: {
      trigger: EmailTemplateTrigger;
      payload: UpsertHotelEmailTemplatePayload;
    }) =>
      hotelEmailTemplateService.upsertByTrigger(
        hotelId as string,
        trigger,
        payload,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: hotelEmailTemplateKeys.list(hotelId),
      });
      toast.success("Email template saved.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save email template.");
    },
  });
};

export const useDeleteHotelEmailTemplate = (hotelId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) =>
      hotelEmailTemplateService.deleteTemplate(hotelId as string, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: hotelEmailTemplateKeys.list(hotelId),
      });
      toast.success("Email template reset to default.");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to reset email template.");
    },
  });
};

export const usePreviewHotelEmailTemplate = (hotelId: string | null) => {
  return useMutation<
    RenderedHotelEmailTemplate,
    Error,
    PreviewHotelEmailTemplatePayload
  >({
    mutationFn: (payload) =>
      hotelEmailTemplateService.previewUnsaved(hotelId as string, payload),
    onError: (error) => {
      toast.error(error.message || "Failed to render email preview.");
    },
  });
};
